import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TablePagination } from '@mui/material';
import { MdLock } from 'react-icons/md';
import { useAuth } from '../auth/AuthContext';
import { useBootstrapReadyState } from '../context/BootstrapReadyContext';
import { OUTLOOK_PREMIUM_COLUMN_KEYS } from '../utils/outlookPremiumAccess';
import UpgradeToPremiumBanner from '../components/UpgradeToPremiumBanner';
import { SymbolWithTradingView, symbolCellTdStyle } from '../components/TradingViewLink';
import FiiSectorFlowsSection from '../components/FiiSectorFlowsSection';
import { getTradingViewChartSymbol } from '../utils/tradingViewOutlookSymbols';
import { buildMarketBarChart } from '../utils/marketBarChart';
import {
  fetchMarketOutlookBundle,
  loadFiiDiiWithCache,
  marketOutlookHasUsable,
  normalizeMarketOutlookPayload,
} from '../utils/marketOutlookLoader';
import {
  buildFiiDiiCard,
  FII_DII_FETCH_DAYS,
  fmtCr,
  periodCircleShownPoint,
} from '../utils/fiiDiiPayload';
import { LIVE_PAGE_CACHE_KEYS } from '../utils/livePageCacheKeys';
import { runLiveMarketPageMountPoll, runScreenPayloadFetch } from '../utils/screenPageLoader';
import {
  CardContainer,
  Card,
  CardHeader,
  CardValue,
  CardChange,
  CardStats,
  CashCard,
  CashTitle,
  CashSubtitle,
  CashValue,
  BarChart,
  CashCardContainer,
  SmallCardContainer,
  SmallFull,
  SmallHalf,
  FiiDiiPeriodSection,
  FiiDiiPeriodRow,
  FiiDiiPeriodItem,
  FiiDiiPeriodLabel,
  FiiDiiPeriodCircle,
  TableSection,
  TableTitle,
  TableWrapper,
  Table
} from './MarketOutlook.styles';

import {
  ensureMarketSession,
  formatIstTime,
  getCachedMarketSession,
  getMarketPollingIntervalMs,
  isAfterNseCloseIstNow,
  isPageCacheStale,
  shouldPollLiveMarket,
} from '../utils/marketSession';
import { readPageCache } from '../utils/pageDataCache';

const MARKET_OUTLOOK_CACHE_KEY = LIVE_PAGE_CACHE_KEYS.marketOutlook;
const FII_DII_CACHE_KEY = LIVE_PAGE_CACHE_KEYS.fiiDii;
const MARKET_REFRESH_MS = 30000;
const INDICES_TABLE_ROWS_PER_PAGE_OPTIONS = [10, 15, 25, 50];

const PLACEHOLDER_OUTLOOK_CARD = {
  title: '—',
  trend: 'SIDEWAYS',
  value: '—',
  change: '—',
  percentile: '—',
  pe: '—',
};
const EMPTY_OUTLOOK_CARD_ROW = [
  PLACEHOLDER_OUTLOOK_CARD,
  PLACEHOLDER_OUTLOOK_CARD,
  PLACEHOLDER_OUTLOOK_CARD,
];

function FiiDiiPeriodCircles({ title, circles, yearRow = false, onHover }) {
  if (!circles?.length) return null;

  return (
    <FiiDiiPeriodSection role="group" aria-label={title}>
      <FiiDiiPeriodRow className={yearRow ? 'year-row' : undefined}>
        {circles.map((circle) => (
          <FiiDiiPeriodItem key={`${title}-${circle.label}`}>
            <FiiDiiPeriodLabel>{circle.label}</FiiDiiPeriodLabel>
            <FiiDiiPeriodCircle
              $color={circle.color}
              $neutral={!circle.hasData}
              role="button"
              tabIndex={circle.hasData ? 0 : -1}
              aria-label={circle.ariaLabel}
              title={circle.ariaLabel}
              onMouseEnter={() => onHover?.(periodCircleShownPoint(circle))}
              onMouseLeave={() => onHover?.(null)}
              onFocus={() => onHover?.(periodCircleShownPoint(circle))}
              onBlur={() => onHover?.(null)}
            >
              {circle.display}
            </FiiDiiPeriodCircle>
          </FiiDiiPeriodItem>
        ))}
      </FiiDiiPeriodRow>
    </FiiDiiPeriodSection>
  );
}

function FiiDiiPeriodTotals({ card }) {
  const totals = [
    ['MTD', card.mtdNet],
    ['QTD', card.qtdNet],
    ['YTD', card.ytdNet],
  ];
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
      {totals.map(([label, net]) => (
        <div key={label} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#555' }}>{label}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: net == null ? '#9aa0a6' : net >= 0 ? '#28a745' : '#dc3545' }}>
            {fmtCr(net)}
          </span>
        </div>
      ))}
    </div>
  );
}

function MarketOutlookContent({ apiReady, timedOut }) {
  const { outlookPremium } = useAuth();
  const [searchParams] = useSearchParams();
  const miDiag = searchParams.get('mi_diag') === '1';

  const [fiiDiiData, setFiiDiiData] = useState(null);
  const [fiiDiiLoadState, setFiiDiiLoadState] = useState('idle'); // idle | loading | error | done

  const [indexCards, setIndexCards] = useState(EMPTY_OUTLOOK_CARD_ROW);
  const [smallcapCards, setSmallcapCards] = useState(EMPTY_OUTLOOK_CARD_ROW);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [fiiHoverIdx, setFiiHoverIdx] = useState(null);
  const [diiHoverIdx, setDiiHoverIdx] = useState(null);
  const [fiiPeriodHover, setFiiPeriodHover] = useState(null);
  const [diiPeriodHover, setDiiPeriodHover] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [isLiveRefresh, setIsLiveRefresh] = useState(false);
  const [dataLooksStale, setDataLooksStale] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const applyMarketCache = (payload) => {
      const normalized = normalizeMarketOutlookPayload(payload);
      if (!normalized) return;
      if (normalized.indexCards?.length) setIndexCards(normalized.indexCards);
      if (normalized.smallcapCards?.length) {
        const cards = normalized.smallcapCards;
        const pad = () => ({ ...PLACEHOLDER_OUTLOOK_CARD });
        const padded = [...cards.slice(0, 3), ...Array(Math.max(0, 3 - cards.length)).fill(null).map(pad)];
        setSmallcapCards(padded.slice(0, 3));
      }
      if (normalized.tableData) setTableData(normalized.tableData);
      if (normalized.lastRefreshedAt) setLastRefreshedAt(new Date(normalized.lastRefreshedAt));
    };

    const syncMarketSessionFlags = () => {
      const session = getCachedMarketSession();
      const liveSession = shouldPollLiveMarket(session);
      const cachedWrap = readPageCache(MARKET_OUTLOOK_CACHE_KEY);
      const cacheStale = isPageCacheStale(cachedWrap?.updatedAt, session);
      if (isMounted) {
        setIsLiveRefresh(liveSession);
        setDataLooksStale(cacheStale);
      }
    };

    const load = async ({ silent = false, forceNetwork = false } = {}) => {
      if (!silent) setLoadError(null);
      await ensureMarketSession();
      syncMarketSessionFlags();
      await runScreenPayloadFetch({
        cacheKey: MARKET_OUTLOOK_CACHE_KEY,
        fetcher: () => fetchMarketOutlookBundle({ miDiag }),
        applyPayload: (payload) => {
          applyMarketCache(payload);
          if (isMounted) setDataLooksStale(false);
        },
        setLoading: (v) => { if (!silent && isMounted) setIsLoading(v); },
        setError: (msg) => { if (!silent && isMounted) setLoadError(msg); },
        forceNetwork,
        hasUsable: marketOutlookHasUsable,
        silent,
      });
    };

    const loadFiiDii = async ({ silent = false, forceNetwork = false } = {}) => {
      if (!isMounted) return;
      await loadFiiDiiWithCache({
        cacheKey: FII_DII_CACHE_KEY,
        minDays: FII_DII_FETCH_DAYS,
        setData: setFiiDiiData,
        setLoadState: setFiiDiiLoadState,
        forceNetwork,
        silent,
      });
    };

    let marketCleanup;
    let fiiTimer;
    (async () => {
      await runLiveMarketPageMountPoll({
        load,
        liveIntervalMs: MARKET_REFRESH_MS,
        onCleanup: (fn) => { marketCleanup = fn; },
      });
      await loadFiiDii();
      if (!isMounted) return;
      await ensureMarketSession();
      const fiiPollMs = getMarketPollingIntervalMs(MARKET_REFRESH_MS, 30 * 60 * 1000);
      if (fiiPollMs > 0) {
        fiiTimer = setInterval(() => loadFiiDii({ silent: true, forceNetwork: true }), fiiPollMs);
      }
    })();

    return () => {
      isMounted = false;
      marketCleanup?.();
      clearInterval(fiiTimer);
    };
  }, [miDiag]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(15);

  const sortedTableData = React.useMemo(() => {
    const sortableItems = [...tableData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Attempt numeric sort, otherwise string sort
        // Remove symbols for numbers and parse
        const numericKeys = [
          'value', 'percentile', 'day1d', 'week1w', 'month1m', 'month3m', 'month6m', 'year1y', 'year3y'
        ];
        if (numericKeys.includes(sortConfig.key)) {
          aValue = parseFloat((aValue || '').replace(/[^\d.-]/g, ''));
          bValue = parseFloat((bValue || '').replace(/[^\d.-]/g, ''));
          aValue = isNaN(aValue) ? -Infinity : aValue;
          bValue = isNaN(bValue) ? -Infinity : bValue;
        }
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [tableData, sortConfig]);

  const paginatedTableRows = useMemo(() => {
    const start = tablePage * tableRowsPerPage;
    return sortedTableData.slice(start, start + tableRowsPerPage);
  }, [sortedTableData, tablePage, tableRowsPerPage]);

  const requestSort = (key) => {
    if (!outlookPremium && OUTLOOK_PREMIUM_COLUMN_KEYS.has(key)) return;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setTablePage(0);
  };

  // Column configuration for header rendering
  const columnConfig = [
    { key: 'id', label: '#' },
    { key: 'name', label: 'Index' },
    { key: 'trend', label: 'Trend' },
    { key: 'value', label: 'CMP' },
    { key: 'percentile', label: 'Percentile' },
    { key: 'day1d', label: '1D' },
    { key: 'week1w', label: '1W' },
    { key: 'month1m', label: '1M' },
    { key: 'month3m', label: '3M' },
    { key: 'month6m', label: '6M' },
    { key: 'year1y', label: '1Y' },
    { key: 'year3y', label: '3Y' }
  ];

  const fmtCrDisplay = fmtCr;

  const effectiveYear = fiiDiiData?.effective_year ?? fiiDiiData?.effectiveYear ?? new Date().getFullYear();

  const fiiCard = useMemo(() => {
    if (!fiiDiiData) {
      return {
        value: '—',
        latestNet: null,
        latestDate: '',
        mtdNet: null,
        bars: [],
        series: [],
        quarters: [],
        years: [],
      };
    }
    const card = buildFiiDiiCard(fiiDiiData, 'fii');
    return {
      ...card,
      value: card.latestDate ? fmtCrDisplay(card.latestNet) : card.mtdNet !== 0 ? fmtCrDisplay(card.mtdNet) : '—',
    };
  }, [fiiDiiData]);

  const diiCard = useMemo(() => {
    if (!fiiDiiData) {
      return {
        value: '—',
        latestNet: null,
        latestDate: '',
        mtdNet: null,
        bars: [],
        series: [],
        quarters: [],
        years: [],
      };
    }
    const card = buildFiiDiiCard(fiiDiiData, 'dii');
    return {
      ...card,
      value: card.latestDate ? fmtCrDisplay(card.latestNet) : card.mtdNet !== 0 ? fmtCrDisplay(card.mtdNet) : '—',
    };
  }, [fiiDiiData]);

  const fiiShownPoint = useMemo(() => {
    if (fiiPeriodHover) return fiiPeriodHover;
    if (!fiiCard.series?.length) return { net: fiiCard.latestNet, date: fiiCard.latestDate };
    if (fiiHoverIdx != null && fiiCard.series[fiiHoverIdx]) {
      return fiiCard.series[fiiHoverIdx];
    }
    return fiiCard.series[fiiCard.series.length - 1];
  }, [fiiCard, fiiHoverIdx, fiiPeriodHover]);

  const diiShownPoint = useMemo(() => {
    if (diiPeriodHover) return diiPeriodHover;
    if (!diiCard.series?.length) return { net: diiCard.latestNet, date: diiCard.latestDate };
    if (diiHoverIdx != null && diiCard.series[diiHoverIdx]) {
      return diiCard.series[diiHoverIdx];
    }
    return diiCard.series[diiCard.series.length - 1];
  }, [diiCard, diiHoverIdx, diiPeriodHover]);

  const getTrendClass = (trend) => {
    if (/bullish|up|↗/i.test(trend)) return 'up';
    if (/bearish|down|↘/i.test(trend)) return 'down';
    return 'sideways';
  };

  const extractSignedNumber = (value) => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const text = String(value).trim();
    if (!text) return null;
    const match = text.match(/-?\d+(?:\.\d+)?/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getDailySignClass = (value) => {
    const n = extractSignedNumber(value);
    if (n == null || n === 0) return '';
    return n > 0 ? 'up' : 'down';
  };

  /** Row background follows trend bucket (SIDEWAYS gets a tint; avoids “missing” highlight on ~0% days). */
  const getMarketIndexRowClass = (row) => {
    if (row.trendDirection === 'up') return 'row-up';
    if (row.trendDirection === 'down') return 'row-down';
    return 'row-sideways';
  };

  const getMarketIndexTrendCellClass = (row) => {
    if (row.trendDirection === 'up') return 'trend-up';
    if (row.trendDirection === 'down') return 'trend-down';
    return 'trend-sideways';
  };

  const perfCellClassName = (cell) => {
    const s = (cell ?? '').toString().trim();
    if (!s || s === '—') return '';
    return s.includes('-') ? 'trend-down' : 'trend-up';
  };

  // Sort arrow helper similar to SectorOutlookPage.js
  const getSortArrow = (columnKey) => {
    if (sortConfig.key !== columnKey) return ' ⬍';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const marketSession = getCachedMarketSession();

  return (
    <>
      {timedOut && !apiReady && (
        <div style={{ marginBottom: '12px', color: '#b45309', fontWeight: 600, fontSize: 14 }}>
          Could not reach the API within 60s — the server may be busy during market hours. Wait a moment and refresh.
        </div>
      )}
      {loadError && (
        <div style={{ marginBottom: '12px', color: '#dc3545', fontWeight: 600 }}>
          {loadError}
        </div>
      )}
      {isLoading && !loadError && (
        <div style={{ marginBottom: '12px', color: '#666', fontWeight: 600 }}>
          Loading market indices...
        </div>
      )}
      {lastRefreshedAt && (
        <div
          style={{
            marginBottom: '12px',
            color: dataLooksStale ? '#b45309' : '#666',
            fontSize: 12,
            fontWeight: dataLooksStale ? 600 : 400,
          }}
        >
          {isLiveRefresh
            ? `Live refresh every 30s. Last update: ${formatIstTime(lastRefreshedAt)}`
            : marketSession?.isTradingDay && isAfterNseCloseIstNow()
              ? `Market closed — today's session close. Updated: ${formatIstTime(lastRefreshedAt)}`
              : `Market closed — showing last available data. Updated: ${formatIstTime(lastRefreshedAt)}`}
          {dataLooksStale ? ' — fetching latest data…' : ''}
        </div>
      )}
      <UpgradeToPremiumBanner />
      {/* Index Cards Row 1 */}
      <CardContainer>
        {indexCards.map((card, idx) => (
          <Card key={idx}>
            <CardHeader>
              <div>
                <h3>{card.title}</h3>
                <span className={`trend-badge ${getDailySignClass(card.change) || card.trendDirection || getTrendClass(card.trend)}`}>{card.trend}</span>
              </div>
            </CardHeader>
            <CardValue>{card.value}</CardValue>
            <CardChange className={(card.change || '').toString().startsWith('-') ? 'trend-down' : 'trend-up'}>{card.change}</CardChange>
            <CardStats>
              <span>{card.percentile} Percentile</span>
              <span>|</span>
              <span>{card.pe}</span>
            </CardStats>
          </Card>
        ))}
      </CardContainer>

      {/* Cash Cards + Smallcap Cards Row 2 */}
      <CashCardContainer>
        {/* FII Cash - Left Column */}
        {(() => {
          const bars = buildMarketBarChart(fiiCard.bars, fiiHoverIdx);
          return (
            <CashCard>
              <CashTitle>FII Cash</CashTitle>
              <CashSubtitle>Foreign Institutional Investors</CashSubtitle>
              <CashValue style={{ color: (fiiShownPoint.net ?? 0) >= 0 ? '#28a745' : '#dc3545' }}>
                {fmtCr(fiiShownPoint.net)}
              </CashValue>
              {fiiShownPoint.date && (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{fiiShownPoint.date}</div>
              )}
              <FiiDiiPeriodTotals card={fiiCard} />
              <BarChart>
                {bars ? (
                  <svg
                    viewBox={`0 0 ${bars.width} ${bars.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    onMouseLeave={() => setFiiHoverIdx(null)}
                  >
                    <line x1="0" y1={bars.midY} x2={bars.width} y2={bars.midY} stroke="#ccc" strokeWidth="0.5" />
                    {bars.rects.map((r, i) => (
                      <rect
                        key={i}
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={Math.max(r.h, 0.5)}
                        fill={r.fill}
                        rx="1"
                        opacity={r.isActive ? 1 : 0.9}
                        stroke={r.isActive ? '#1a3c5e' : 'none'}
                        strokeWidth={r.isActive ? 0.8 : 0}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setFiiHoverIdx(r.i)}
                      />
                    ))}
                  </svg>
                ) : (
                  <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet">
                    <line x1="0" y1="30" x2="100" y2="30" stroke="#ccc" strokeWidth="1" strokeDasharray="4" />
                    <text x="50" y="20" textAnchor="middle" fontSize="8" fill="#bbb">
                      {fiiDiiLoadState === 'loading' ? 'Loading…' : 'No chart data'}
                    </text>
                  </svg>
                )}
              </BarChart>
              <FiiDiiPeriodCircles
                title={`FII quarterly activity ${effectiveYear}`}
                circles={fiiCard.quarters}
                onHover={setFiiPeriodHover}
              />
              <FiiDiiPeriodCircles
                title="FII yearly activity"
                circles={fiiCard.years}
                yearRow
                onHover={setFiiPeriodHover}
              />
            </CashCard>
          );
        })()}

        {/* DII Cash - Right Column */}
        {(() => {
          const bars = buildMarketBarChart(diiCard.bars, diiHoverIdx);
          return (
            <CashCard>
              <CashTitle>DII Cash</CashTitle>
              <CashSubtitle>Domestic Institutional Investors</CashSubtitle>
              <CashValue style={{ color: (diiShownPoint.net ?? 0) >= 0 ? '#28a745' : '#dc3545' }}>
                {fmtCr(diiShownPoint.net)}
              </CashValue>
              {diiShownPoint.date && (
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{diiShownPoint.date}</div>
              )}
              <FiiDiiPeriodTotals card={diiCard} />
              <BarChart>
                {bars ? (
                  <svg
                    viewBox={`0 0 ${bars.width} ${bars.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    onMouseLeave={() => setDiiHoverIdx(null)}
                  >
                    <line x1="0" y1={bars.midY} x2={bars.width} y2={bars.midY} stroke="#ccc" strokeWidth="0.5" />
                    {bars.rects.map((r, i) => (
                      <rect
                        key={i}
                        x={r.x}
                        y={r.y}
                        width={r.w}
                        height={Math.max(r.h, 0.5)}
                        fill={r.fill}
                        rx="1"
                        opacity={r.isActive ? 1 : 0.9}
                        stroke={r.isActive ? '#1a3c5e' : 'none'}
                        strokeWidth={r.isActive ? 0.8 : 0}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setDiiHoverIdx(r.i)}
                      />
                    ))}
                  </svg>
                ) : (
                  <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet">
                    <line x1="0" y1="30" x2="100" y2="30" stroke="#ccc" strokeWidth="1" strokeDasharray="4" />
                    <text x="50" y="20" textAnchor="middle" fontSize="8" fill="#bbb">
                      {fiiDiiLoadState === 'loading' ? 'Loading…' : 'No chart data'}
                    </text>
                  </svg>
                )}
              </BarChart>
              <FiiDiiPeriodCircles
                title={`DII quarterly activity ${effectiveYear}`}
                circles={diiCard.quarters}
                onHover={setDiiPeriodHover}
              />
              <FiiDiiPeriodCircles
                title="DII yearly activity"
                circles={diiCard.years}
                yearRow
                onHover={setDiiPeriodHover}
              />
            </CashCard>
          );
        })()}

        <SmallCardContainer>
          <SmallFull>
            <CardHeader>
              <div>
                <h3>{smallcapCards[0].title}</h3>
                    <span className={`trend-badge ${getDailySignClass(smallcapCards[0].change) || smallcapCards[0].trendDirection || getTrendClass(smallcapCards[0].trend)}`}>{smallcapCards[0].trend}</span>
              </div>
            </CardHeader>
            <CardValue>{smallcapCards[0].value}</CardValue>
            <CardChange className={(smallcapCards[0].change || '').toString().startsWith('-') ? 'trend-down' : 'trend-up'}>{smallcapCards[0].change}</CardChange>
            <CardStats>
              <span>{smallcapCards[0].percentile} Percentile</span>
              <span>|</span>
              <span>{smallcapCards[0].pe}</span>
            </CardStats>
          </SmallFull>

          {[1, 2].map((i) => {
            const sc = smallcapCards[i];
            return (
              <SmallHalf key={i}>
                <CardHeader>
                  <div>
                    <h3>{sc.title}</h3>
                    <span className={`trend-badge ${getDailySignClass(sc.change) || sc.trendDirection || getTrendClass(sc.trend)}`}>{sc.trend}</span>
                  </div>
                </CardHeader>
                <CardValue>{sc.value}</CardValue>
                <CardChange className={(sc.change || '').toString().startsWith('-') ? 'trend-down' : 'trend-up'}>{sc.change}</CardChange>
                <CardStats>
                  <span>{sc.percentile} Percentile</span>
                  <span>|</span>
                  <span>{sc.pe}</span>
                </CardStats>
              </SmallHalf>
            );
          })}
        </SmallCardContainer>
      </CashCardContainer>

      <FiiSectorFlowsSection />

      {/* Table Section */}
      <TableSection>
        <TableTitle>Market Indices</TableTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                {columnConfig.map((col) => {
                  const locked = !outlookPremium && OUTLOOK_PREMIUM_COLUMN_KEYS.has(col.key);
                  return (
                    <th
                      key={col.key}
                      onClick={() => requestSort(col.key)}
                      style={{ cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.75 : 1 }}
                      title={locked ? 'Premium access required' : undefined}
                    >
                      {col.label}
                      {locked ? ' 🔒' : ''}
                      {getSortArrow(col.key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedTableRows.map((row) => (
                <tr key={row.id} className={getMarketIndexRowClass(row)}>
                  <td className="index">{row.id}</td>
                  <td style={symbolCellTdStyle({}, 160)}>
                    <SymbolWithTradingView
                      chartSymbol={getTradingViewChartSymbol(row.name)}
                      iconFirst
                      gap={6}
                    >
                      {row.name}
                    </SymbolWithTradingView>
                  </td>
                  <td className={getMarketIndexTrendCellClass(row)}>{row.trend}</td>
                  <td>{row.value}</td>
                  <td><span className="percentage">{row.percentile}</span></td>
                  <td
                    className={perfCellClassName(row.day1d)}
                    title={row.perf1dTitle || undefined}
                  >
                    {row.day1d}
                  </td>
                  <td className={perfCellClassName(row.week1w)}>{row.week1w}</td>
                  <td className={perfCellClassName(row.month1m)}>{row.month1m}</td>
                  <td className={perfCellClassName(row.month3m)}>{row.month3m}</td>
                  {!outlookPremium && OUTLOOK_PREMIUM_COLUMN_KEYS.has('month6m') ? (
                    <td style={{ textAlign: 'center', color: '#bdbdbd' }} title="Premium access required">
                      <MdLock size={18} style={{ verticalAlign: 'middle' }} />
                    </td>
                  ) : (
                    <td className={perfCellClassName(row.month6m)}>{row.month6m}</td>
                  )}
                  {!outlookPremium && OUTLOOK_PREMIUM_COLUMN_KEYS.has('year1y') ? (
                    <td style={{ textAlign: 'center', color: '#bdbdbd' }} title="Premium access required">
                      <MdLock size={18} style={{ verticalAlign: 'middle' }} />
                    </td>
                  ) : (
                    <td className={perfCellClassName(row.year1y)}>{row.year1y}</td>
                  )}
                  {!outlookPremium && OUTLOOK_PREMIUM_COLUMN_KEYS.has('year3y') ? (
                    <td style={{ textAlign: 'center', color: '#bdbdbd' }} title="Premium access required">
                      <MdLock size={18} style={{ verticalAlign: 'middle' }} />
                    </td>
                  ) : (
                    <td className={perfCellClassName(row.year3y)}>{row.year3y}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
          <TablePagination
            component="div"
            count={sortedTableData.length}
            page={tablePage}
            onPageChange={(_e, p) => setTablePage(p)}
            rowsPerPage={tableRowsPerPage}
            onRowsPerPageChange={(e) => {
              setTableRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
            }}
            rowsPerPageOptions={INDICES_TABLE_ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage="Rows per page"
          />
        </TableWrapper>
      </TableSection>
    </>
  );
}

function MarketOutlookPage() {
  const { showData, apiReady, timedOut } = useBootstrapReadyState();

  if (!showData) {
    return (
      <div style={{ padding: '48px 24px', textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
        <div style={{ marginBottom: 16, color: '#333', fontWeight: 600, fontSize: 18 }}>
          Connecting to API…
        </div>
        <div style={{ color: '#666', fontSize: 14, lineHeight: 1.5 }}>
          Waiting for <code style={{ fontSize: 13 }}>GET /api/health</code> (lightweight API liveness).
        </div>
      </div>
    );
  }

  return (
    <MarketOutlookContent apiReady={apiReady} timedOut={timedOut} />
  );
}

export default MarketOutlookPage;